import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, Response, shouldRenderMobile, ToastService } from '@philips-emr/hpms-provider-commons';
import { AttachmentsListItem } from '@philips-emr/hpms-provider-components/src/api/attachments-list-api';
import { ActionAPI, DeckAPI, FetchAPI, FormAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color, ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelect, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SelectEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService, SessionStorageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { TechnicalVisitInconsistenciesAliases as Aliases } from '../../codegen/constants/aliases/technical-visit-inconsistencies-aliases';
import BaseController from '../BaseController';

import './technical-visit-controller.css';
import { TechnicalVisitStatus } from './technical-visit-controller';

interface Inconsistecy {
  ieStatus: InconsistecyStatus,
  dsStatus: string,
  dsSent: string,
  daysLeft: string,
  ieDelayed: string,
  dsDelayed: string,
  inconsistency: string,
  nrSeqInconsistencyVisit: number,
  limitDate: Date,
  allowedPostponeInconsistency: string,
  statusTechnicalVisit: number,
}

interface ReplyExtensionRequests {
  ieStatus: ReplyExtensionRequestsStatus,
  dsStatus: string,
}

enum ReplyExtensionRequestsStatus {
  DO_NOT_APPLY = 0,
  PENDING = 1,
  CONFIRMED = 2,
  REFUSED = 3,
  CANCELED = 4,
}

enum InconsistecyStatus {
  DELAYED = 0,
  PENDING = 1,
  AWAITING_RETURN = 2,
  AWAITING_APPROVAL = 3,
  CONFIRMED = 4,
  DISAPPROVED = 5,
}

interface AttachmentsListItemDetail extends AttachmentsListItem {
  nrSequence: number;
  dsAttachment: string;
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_INCONSISTENCIES })
class TechnicalVisiInconsistenciesController extends BaseController {

  @Inject(Aliases.FETCH_FORM)
  private fetchForm: FetchAPI;
  @Inject(Aliases.DECK_INCONSISTENCY)
  private deckInconsistency: DeckAPI;
  @Inject(Aliases.FETCH_INCONSISTENCY)
  private fetchInconsistency: FetchAPI;
  @Inject(Aliases.FETCH_ATTACHMENT_LIST)
  private fetchAttachmentsList: FetchAPI;
  @Inject(Aliases.FORM_APPOINTMENT_DATA)
  private formAppointmentData: FormAPI;
  @Inject(Aliases.PANEL_MODAL_REFUSE)
  private panelModalRefuse: PanelAPI;
  @Inject(Aliases.ACTION_POSTPONE)
  private actionPostpone: ActionAPI;
  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;
  @Inject(Aliases.FETCH_REQUESTED_DATE)
  private fetchRequestedDate: FetchAPI;
  @Inject(Aliases.PANEL_REQUESTED_DATE)
  private panelRequestedDate: PanelAPI;

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<AttachmentsListItem>): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { nrSequence, dsAttachment } = event.value as AttachmentsListItemDetail;
    ToastService.open({ level: ToastLevel.INFORMATION, message: MessageService.translate('provider.download-initiated') });
    FileService.download(Endpoint.GET.TECHNICAL_VISIT_ATTACHMENT_DOWNLOAD, dsAttachment,
      { nrSeqAttachment: nrSequence, nrSeqVisit }, HttpMethod.GET)
      .catch(() => {
        ToastService.open({ level: ToastLevel.ERROR, message: MessageService.translate('provider.error-finding-file') });
      });
  }

  @OnMount([Aliases.FETCH_INCONSISTENCY, Aliases.FETCH_MENUS_MOBILE])
  onMountDataFetch({ target }: MountEvent): void {
    const api = target as FetchAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqVisit });
    api.forceFetch();
  }

  @OnDynamicContentRender(Aliases.CARD_INCONSISTENCY)
  onRenderContentInconsistency({ data, render }: RenderEvent): void {
    const { ieStatus, daysLeft, ieDelayed } = data as Inconsistecy;
    const msgStamp = ieDelayed == 'S' ? '' : daysLeft;
    const label = ieStatus == 3 ? '' : msgStamp;
    const element = getRenderInconsistency(ieStatus, label, true);
    if (element) {
      render((): ReactElement => element);
    }
  }

  @OnDynamicContentRender(Aliases.FIELD_STATUS)
  onRenderAppointmentsHistory(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsSent, dsStatus, ieDelayed, dsDelayed } = data as Inconsistecy;
    const msgStamp = ieDelayed == 'S' ? dsDelayed : dsStatus;
    const label = ieStatus == 3 ? dsSent : msgStamp;
    const element = getRenderInconsistency(ieStatus, label);
    if (element) {
      render((): ReactElement => element);
    }
  }

  @OnAfterFetch([Aliases.FETCH_INCONSISTENCY, Aliases.FETCH_MENUS_MOBILE])
  onAfterFetchHistories(event: FetchEvent): void {
    const data = event.data as Inconsistecy[];
    if (data) {
      const { nrSeqInconsistencyVisit: nrSequence, allowedPostponeInconsistency, statusTechnicalVisit } = data[0];
      let nrSeqInconsistencyVisit = SessionStorageService.getItem('nrSeqInconsistencyVisit');
      SessionStorageService.removeItem('nrSeqInconsistencyVisit');
      if (nrSeqInconsistencyVisit) {
        this.getInconsistency(data, nrSeqInconsistencyVisit);
      }
      else {
        this.actionPostpone.setEnabled(allowedPostponeInconsistency == 'S' && statusTechnicalVisit == TechnicalVisitStatus.EVALUATING_INCONSISTENCIES);
        nrSeqInconsistencyVisit = nrSequence;
      }
      const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
      this.fetchForm.setParameters({ nrSeqVisit, nrSequence: nrSeqInconsistencyVisit });
      this.fetchForm.forceFetch();
      this.context.set({ nrSeqVisit, nrSeqInconsistencyVisit });
      this.fetchAttachmentsList.setParameters({ nrSeqVisit, nrSeqInconsistencyVisit });
      this.fetchAttachmentsList.forceFetch();
      if (!shouldRenderMobile()) {
        this.deckInconsistency.selectCard(String(nrSeqInconsistencyVisit));
      }
    }
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<{ nrSeqInconsistencyVisit: string }>): void {
    const nrSeqInconsistencyVisit = event.value.nrSeqInconsistencyVisit;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.fetchForm.setParameters({ nrSeqVisit, nrSequence: nrSeqInconsistencyVisit });
    this.fetchForm.forceFetch();
    this.context.set({ nrSeqVisit, nrSeqInconsistencyVisit });
    this.fetchAttachmentsList.setParameters({ nrSeqVisit, nrSeqInconsistencyVisit });
    this.fetchAttachmentsList.forceFetch();
  }

  @OnSelectItem(Aliases.DECK_INCONSISTENCY)
  onSelectInconsistency(event: SelectItemEvent<Inconsistecy>): void {
    const { nrSeqInconsistencyVisit, allowedPostponeInconsistency, statusTechnicalVisit } = event.item;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.panelRequestedDate.setVisible(true);
    this.actionPostpone.setEnabled(allowedPostponeInconsistency == 'S' && statusTechnicalVisit == TechnicalVisitStatus.EVALUATING_INCONSISTENCIES);
    this.fetchForm.setParameters({ nrSeqVisit, nrSequence: nrSeqInconsistencyVisit });
    this.fetchForm.forceFetch();
    this.context.set({ nrSeqVisit, nrSeqInconsistencyVisit });
    this.fetchAttachmentsList.setParameters({ nrSeqVisit, nrSeqInconsistencyVisit });
    this.fetchAttachmentsList.forceFetch();
    this.fetchAttachmentsList.setParameters({ nrSeqInconsistencyVisit });
    this.fetchRequestedDate.forceFetch();
  }

  @OnSubmit(Aliases.FORM_APPOINTMENT_DATA)
  onSubmitForm(event: SubmitEvent): void {
    const record = event.record;
    const api = event.target as FormAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const body = { ...record, nrSeqVisit };
    const { nrSeqInconsistencyVisit } = body as unknown as Inconsistecy;
    void HttpService.post(Endpoint.POST.TECHNICAL_VISIT_INCONSISTENCY_ACTION, { nrSeqVisit }, { body })
      .then(() => {
        SessionStorageService.setItem('nrSeqInconsistencyVisit', nrSeqInconsistencyVisit);
        this.fetchInconsistency.setParameters({ nrSeqVisit });
        this.fetchInconsistency.forceFetch();
        api.setRecord({});
        api.setEditing(false);
        this.fetchAttachmentsList.forceFetch();
        ToastService.open({ level: ToastLevel.SUCCESS, message: MessageService.translate('provider.success-postpone-date') });
      }).catch((resp) => {
        const { response } = resp.body as unknown as Response;
        ToastService.open({ level: ToastLevel.ERROR, message: response });
      });
  }

  @OnMount(Aliases.FETCH_ATTACHMENT_LIST)
  onCancelForm(): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { nrSeqInconsistencyVisit } = this.context.getBulkData();
    this.fetchAttachmentsList.setParameters({ nrSeqVisit, nrSeqInconsistencyVisit });
    this.fetchAttachmentsList.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_FORM)
  onAfterFetchForm(event: FetchEvent): void {
    if (event.data) {
      const { ieStatus, ieDelayed, statusTechnicalVisit, inconsistency, nrSeqInconsistencyVisit } = event.data as Inconsistecy;
      this.formAppointmentData.setEditing((ieStatus == InconsistecyStatus.AWAITING_RETURN && ieDelayed == 'N') && statusTechnicalVisit == TechnicalVisitStatus.EVALUATING_INCONSISTENCIES);
      this.formAppointmentData.setLocked((ieStatus !=  InconsistecyStatus.AWAITING_RETURN || ieDelayed == 'S') || statusTechnicalVisit != TechnicalVisitStatus.EVALUATING_INCONSISTENCIES);
      this.formAppointmentData.setDisabled((ieStatus !=  InconsistecyStatus.AWAITING_RETURN || ieDelayed == 'S') || statusTechnicalVisit != TechnicalVisitStatus.EVALUATING_INCONSISTENCIES);
      if (shouldRenderMobile()) {
        this.formMenuMobile.setData({ menuMobile: { inconsistency, nrSeqInconsistencyVisit } });
      }
    }
  }

  @OnClick(Aliases.ACTION_POSTPONE)
  onClickPostpone(): void {
    this.panelModalRefuse.setVisible(true);
  }

  @OnClose(Aliases.MODAL_REFUSE)
  onCloseModal(): void {
    this.panelModalRefuse.setVisible(false);
  }

  @OnCancel(Aliases.FORM_POSTPONE)
  onCancelFormmodal(): void {
    this.panelModalRefuse.setVisible(false);
  }

  @OnSubmit(Aliases.FORM_POSTPONE)
  onClickRefuse(event: SubmitEvent): void {
    const { nrSeqInconsistencyVisit } = this.formAppointmentData.getData() as unknown as Inconsistecy;
    const data = event.record;
    this.postponeAnswer(nrSeqInconsistencyVisit, data);
  }

  @OnAfterFetch(Aliases.FETCH_REQUESTED_DATE)
  onAfterFetchRequestedDate(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelRequestedDate.setVisible(false);
    } else {
      this.panelRequestedDate.setVisible(true);
    }
  }

  private postponeAnswer(nrSeqInconsistencyVisit: number, data: Record<string, Value>): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { limitDate } = this.formAppointmentData.getRecord();
    const body = { ...data, nrSeqInconsistencyVisit, limitDate };
    HttpService.post(Endpoint.POST.TECHNICAL_VISIT_POSTPONE_ANSWER, { nrSeqVisit }, { body })
      .then(() => {
        SessionStorageService.setItem('nrSeqInconsistencyVisit', nrSeqInconsistencyVisit);
        this.fetchInconsistency.setParameters({ nrSeqVisit });
        this.fetchInconsistency.forceFetch();
        this.fetchForm.forceFetch();
        this.panelModalRefuse.setVisible(false);
        this.actionPostpone.setEnabled(false);
        this.panelRequestedDate.setVisible(true);
        this.fetchRequestedDate.forceFetch();
        ToastService.open({ level: ToastLevel.SUCCESS, message: MessageService.translate('provider.success-postpone-date') });
      }).catch((resp) => {
        const { response } = resp.body as unknown as Response;
        ToastService.open({ level: ToastLevel.SUCCESS, message: response });
      });
  }

  private getInconsistency(data: Inconsistecy[], nrSeqInconsistencyVisit: number):void {
    data.forEach(inconsistency => {
      const postponeRule = inconsistency.allowedPostponeInconsistency == 'S' && inconsistency.statusTechnicalVisit == TechnicalVisitStatus.EVALUATING_INCONSISTENCIES;
      if (inconsistency.nrSeqInconsistencyVisit == nrSeqInconsistencyVisit) {
        this.actionPostpone.setEnabled(postponeRule);
      }
    });
  }

  @OnDynamicContentRender(Aliases.CARD_POSTPONE)
  onRenderCardPostponeStatus(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as ReplyExtensionRequests;
    const stamp = this.getStamp(ieStatus, dsStatus);
    if (stamp) {
      render((): ReactElement => stamp );
    }
  }

  private getStamp(ieStatus: ReplyExtensionRequestsStatus, label: string): ReactElement | null {
    let stampProps = null;
    switch (ieStatus) {
      case ReplyExtensionRequestsStatus.PENDING:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label };
        break;
      case ReplyExtensionRequestsStatus.CONFIRMED:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
        break;
      case ReplyExtensionRequestsStatus.REFUSED:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label };
        break;
      case ReplyExtensionRequestsStatus.CANCELED:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label };
        break;
      case ReplyExtensionRequestsStatus.DO_NOT_APPLY:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label };
        break;
      default:
        return null;
    }
    return <Stamp {...stampProps} />;
  }
}

function getRenderInconsistency(ieStatus: InconsistecyStatus, label: string, menu?: boolean): ReactElement | null {
  const stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
  const stampDelayed = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
  const stampDaysLeft = { color: Color.SAFETY_YELLOW, icon: StampIcon.CLOCK, label: menu ? '' : label };
  const stampApproved = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label: menu ? '' : label };
  const stampDisapproved = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label: menu ? '' : label };
  let element = null;
  switch (ieStatus) {
    case InconsistecyStatus.DELAYED:
      element = <Stamp {...stampDelayed} />;
      break;
    case InconsistecyStatus.AWAITING_RETURN:
      element = <Stamp {...stampDaysLeft} />;
      break;
    case InconsistecyStatus.AWAITING_APPROVAL:
      element = <Stamp {...stampProps} />;
      break;
    case InconsistecyStatus.CONFIRMED:
      element = <Stamp {...stampApproved} />;
      break;
    case InconsistecyStatus.DISAPPROVED:
      element = <Stamp {...stampDisapproved} />;
      break;
    default:
      return null;
  }
  return element;
}

export {
  TechnicalVisiInconsistenciesController,
  Inconsistecy,
  getRenderInconsistency,
};
